import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import StatusChip from "@entrilia/shared/components/StatusChip";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CollapseAllIcon from "@entrilia/shared/icons/CollapseAllIcon";
import ExpandAllIcon from "@entrilia/shared/icons/ExpandAllIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type {
  WorkflowTemplateActivity,
  WorkflowTemplateVersionDetails,
  WorkflowTemplateVersionDetailsResponse,
} from "../../../../api/types/workflowTypes";
import useUserContext from "../../../../context/useUserContext";
import ChatGptIcon from "@entrilia/shared/icons/ChatGptIcon";
import GeneralPageFooter from "../../../common/GeneralPageFooter";
import { findQuoteRange } from "../instructionQuoteHelper";
import WorkflowAgentLabel from "./WorkflowAgentLabel";
import WorkflowSystemPrompt from "./WorkflowSystemPrompt";
import WorkflowTemplateInstructionsActivityCard, {
  type ActivityPromptState,
} from "./WorkflowTemplateInstructionsActivityCard";

const updateWorkflowTemplateInstructions = withErrorHandling(adminApi.updateWorkflowTemplateInstructions);
const updateActivityInWorkflowTemplate = withErrorHandling(adminApi.updateActivityInWorkflowTemplate);

interface Props {
  workflowTemplate: WorkflowTemplateVersionDetails;
  onTemplateUpdated: (templateDetails: WorkflowTemplateVersionDetailsResponse) => void;
  onActivityUpdated: (activity: WorkflowTemplateActivity) => void;
  focusPromptId?: string;
  focusQuote?: string;
}

const formatModel = (modelName?: string, reasoningEffort?: string) =>
  modelName && reasoningEffort ? `${modelName} · ${reasoningEffort}` : modelName || "-";

// Chrome does not scroll a textarea to a selection set from code, so measure the text above it and scroll by hand.
const scrollTextareaToPosition = (input: HTMLTextAreaElement, position: number) => {
  const fullValue = input.value;
  const inlineHeight = input.style.height;
  input.style.height = "0px";
  input.value = fullValue.slice(0, position);
  const textHeightBeforePosition = input.scrollHeight;
  input.value = fullValue;
  input.style.height = inlineHeight;
  input.scrollTop = Math.max(0, textHeightBeforePosition - input.clientHeight / 2);
};

const WorkflowTemplateInstructions = ({
  workflowTemplate,
  onTemplateUpdated,
  onActivityUpdated,
  focusPromptId,
  focusQuote,
}: Props) => {
  const { isEntriliaUser } = useUserContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const initialActivityPrompts = useMemo<ActivityPromptState[]>(
    () =>
      workflowTemplate.activities
        .filter((activity) => activity.isAiPowered)
        .map((activity) => ({
          activityId: activity.id,
          activityName: activity.name,
          taskSystemPrompt: activity.agentTaskSystemPrompt,
          validationSystemPrompt: activity.agentValidationSystemPrompt,
          taskPrompt: activity.agentTaskPromptUserInput ?? "",
          validationPrompt: activity.agentValidationPromptUserInput ?? "",
        })),
    [workflowTemplate.activities]
  );

  const [workflowInstructions, setWorkflowInstructions] = useState(workflowTemplate.workflowInstructions ?? "");
  const [activityPrompts, setActivityPrompts] = useState(initialActivityPrompts);
  const [updateActiveWorkflows, setUpdateActiveWorkflows] = useState(false);
  const [showSystemPrompts, setShowSystemPrompts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedActivityIds, setExpandedActivityIds] = useState<string[]>(
    activityPrompts.map((activity) => activity.activityId)
  );
  const focusedPromptInputRef = useRef<HTMLTextAreaElement>(null);

  const isFocusPromptKnown =
    focusPromptId === "workflow" || initialActivityPrompts.some((activity) => activity.activityId === focusPromptId);

  useEffect(() => {
    if (!focusPromptId) {
      return;
    }

    if (!isFocusPromptKnown) {
      sendNotification("This step is no longer in the current template.");
      return;
    }

    const input = focusedPromptInputRef.current;
    if (!input) {
      return;
    }

    input.scrollIntoView({ block: "center" });
    input.focus({ preventScroll: true });

    const range = focusQuote ? findQuoteRange(input.value, focusQuote) : "notFound";
    if (typeof range === "object") {
      scrollTextareaToPosition(input, range.start);
      input.setSelectionRange(range.start, range.end);
    }

    if (focusQuote && range === "notFound") {
      sendNotification("These instructions changed after this result.");
    }
  }, [focusPromptId, focusQuote, isFocusPromptKnown, sendNotification]);

  const changedActivities = useMemo(() => {
    const initialById = new Map(initialActivityPrompts.map((activity) => [activity.activityId, activity]));
    return activityPrompts.filter((activity) => {
      const initial = initialById.get(activity.activityId);
      if (!initial) {
        return false;
      }

      return activity.taskPrompt !== initial.taskPrompt || activity.validationPrompt !== initial.validationPrompt;
    });
  }, [activityPrompts, initialActivityPrompts]);

  const hasAnyActivitySystemPrompt = initialActivityPrompts.some(
    (activity) => Boolean(activity.taskSystemPrompt?.trim()) || Boolean(activity.validationSystemPrompt?.trim())
  );
  const hasSupervisorSystemPrompt = Boolean(workflowTemplate.supervisorAgentSystemPrompt?.trim());
  const canToggleSystemPrompts = isEntriliaUser && (hasAnyActivitySystemPrompt || hasSupervisorSystemPrompt);
  const workflowInstructionsChanged = workflowInstructions !== (workflowTemplate.workflowInstructions ?? "");
  const isDirty = workflowInstructionsChanged || changedActivities.length > 0;

  const handleReset = () => {
    setWorkflowInstructions(workflowTemplate.workflowInstructions ?? "");
    setActivityPrompts(initialActivityPrompts);
    setUpdateActiveWorkflows(false);
    setShowSystemPrompts(false);
  };

  const updateActivityPrompt = (activityId: string, field: "taskPrompt" | "validationPrompt", value: string) => {
    setActivityPrompts((current) =>
      current.map((activity) =>
        activity.activityId === activityId
          ? {
              ...activity,
              [field]: value,
            }
          : activity
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);

    if (workflowInstructionsChanged) {
      const [templateResp, updateInstructionsError] = await updateWorkflowTemplateInstructions(workflowTemplate.id, {
        workflowInstructionsUserInput: workflowInstructions.trim(),
        updateActiveWorkflows,
      });

      if (updateInstructionsError) {
        logError(updateInstructionsError, "[WorkflowTemplateInstructions] updateWorkflowTemplateInstructions");
        sendNotificationError(updateInstructionsError.message);
        setSaving(false);
        return;
      }

      onTemplateUpdated(templateResp);
    }

    for (const activity of changedActivities) {
      const [updatedActivity, updateActivityError] = await updateActivityInWorkflowTemplate(
        workflowTemplate.id,
        activity.activityId,
        {
          agentTaskPromptUserInput: activity.taskPrompt.trim(),
          agentValidationPromptUserInput: activity.validationPrompt.trim(),
          updateActiveWorkflows,
        }
      );

      if (updateActivityError) {
        logError(updateActivityError, "[WorkflowTemplateInstructions] updateActivityInWorkflowTemplate");
        sendNotificationError(`Failed to update "${activity.activityName}": ${updateActivityError.message}`);
        setSaving(false);
        return;
      }

      onActivityUpdated(updatedActivity);
    }

    const successMessage =
      workflowInstructionsChanged && changedActivities.length > 0
        ? "Workflow instructions and activity prompts updated successfully"
        : workflowInstructionsChanged
          ? "Workflow instructions updated successfully"
          : "Activity prompts updated successfully";

    sendNotification(successMessage);
    setSaving(false);
  };

  const handleActivityExpandedChange = (activityId: string, newExpanded: boolean) => {
    setExpandedActivityIds((current) =>
      newExpanded ? [...current, activityId] : current.filter((id) => id !== activityId)
    );
  };

  return (
    <>
      <ScrollableFlexContainer sx={{ height: "calc(100% - 52px)" }}>
        <Container maxWidth="lg" sx={{ pl: 3, pr: 2 }}>
          <Stack spacing={4}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle1">AI Models</Typography>
                <StatusChip label="Read-only" color={(t) => t.palette.text.secondary} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <WorkflowAgentLabel agentType="task" />
                <ChatGptIcon />
                <Typography>
                  {formatModel(workflowTemplate.taskAgentModelName, workflowTemplate.taskAgentReasoningEffort)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <WorkflowAgentLabel agentType="validation" />
                <ChatGptIcon />
                <Typography>
                  {formatModel(
                    workflowTemplate.validationAgentModelName,
                    workflowTemplate.validationAgentReasoningEffort
                  )}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <WorkflowAgentLabel agentType="supervisor" />
                <ChatGptIcon />
                <Typography>
                  {formatModel(
                    workflowTemplate.supervisorAgentModelName,
                    workflowTemplate.supervisorAgentReasoningEffort
                  )}
                </Typography>
              </Stack>
            </Stack>

            <Stack spacing={2} sx={{ pt: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle1">Workflow Instructions</Typography>
                <Typography variant="caption" color="textSecondary">
                  Describe your company process here so the AI can make consistent decisions. Use clear if/then rules
                  for this workflow. Explain when a step may be skipped, when extra checks should be added, and when a
                  user should be asked to confirm missing or conflicting information.
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <WorkflowAgentLabel agentType="supervisor" />
                <Stack spacing={2} sx={{ pl: 4 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    inputRef={focusPromptId === "workflow" ? focusedPromptInputRef : undefined}
                    value={workflowInstructions}
                    onChange={(e) => setWorkflowInstructions(e.target.value)}
                  />
                  {isEntriliaUser && showSystemPrompts && workflowTemplate.supervisorAgentSystemPrompt?.trim() && (
                    <WorkflowSystemPrompt value={workflowTemplate.supervisorAgentSystemPrompt} />
                  )}
                </Stack>
              </Stack>
            </Stack>

            {activityPrompts.length > 0 && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="subtitle1">Activity Instructions</Typography>
                  {expandedActivityIds.length === 0 ? (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setExpandedActivityIds(activityPrompts.map((activity) => activity.activityId))}
                      startIcon={<ExpandAllIcon />}
                    >
                      Expand All
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setExpandedActivityIds([])}
                      startIcon={<CollapseAllIcon />}
                    >
                      Collapse All
                    </Button>
                  )}
                </Box>
                {activityPrompts.map((activity) => (
                  <WorkflowTemplateInstructionsActivityCard
                    key={activity.activityId}
                    activity={activity}
                    showSystemPrompts={showSystemPrompts}
                    isEntriliaUser={isEntriliaUser}
                    onPromptChange={updateActivityPrompt}
                    expanded={expandedActivityIds.includes(activity.activityId)}
                    onExpandedChange={(newExpanded) => handleActivityExpandedChange(activity.activityId, newExpanded)}
                    taskPromptInputRef={activity.activityId === focusPromptId ? focusedPromptInputRef : undefined}
                  />
                ))}
              </>
            )}
          </Stack>
        </Container>
      </ScrollableFlexContainer>
      <GeneralPageFooter containerProps={{ sx: { pl: 0, pr: 0 } }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", maxWidth: "lg", width: "100%", mx: "auto", pl: 3, pr: 3 }}
        >
          <Button variant="contained" color="primary" onClick={handleSave} disabled={!isDirty} loading={saving}>
            Save
          </Button>
          <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty}>
            Cancel
          </Button>

          <FormControlLabel
            label="Also update active workflows with these settings"
            disabled={!isDirty || saving}
            control={
              <Checkbox checked={updateActiveWorkflows} onChange={(_, checked) => setUpdateActiveWorkflows(checked)} />
            }
          />

          <HorizontalFill />

          {canToggleSystemPrompts && (
            <FormControlLabel
              label="Show system prompts (read-only)"
              disabled={saving}
              control={<Switch checked={showSystemPrompts} onChange={(_, checked) => setShowSystemPrompts(checked)} />}
            />
          )}
        </Stack>
      </GeneralPageFooter>
    </>
  );
};

export default WorkflowTemplateInstructions;
